import type { Command, CommandKind } from './Command';

export interface CommandHistoryOptions {
  /** Minimum retained undo depth (spec requires ≥ 100). */
  maxDepth?: number;
  /** Injectable clock, so tests can date entries without waiting for a real one. */
  now?: () => number;
}

/**
 * One operation, as the history panel reads it.
 *
 * A flat value type rather than the command itself: the panel is a *report* on the session, and
 * handing it live command objects would let a list get at `undo()` without going through the cursor
 * — which is exactly the bug that makes a history view and the document disagree.
 */
export interface HistoryEntry {
  /** Stable for the life of the session. Survives undo, redo, and travel; never reused. */
  readonly id: number;
  readonly label: string;
  readonly kind: CommandKind;
  /** Wall-clock time the operation was *first* recorded — a merged drag keeps the moment it began. */
  readonly at: number;
  /** How many mergeable commands folded into this entry. 1 for an ordinary edit. */
  readonly steps: number;
}

export interface CommandHistoryState {
  canUndo: boolean;
  canRedo: boolean;
  undoLabel: string | null;
  redoLabel: string | null;
  /** How many entries are applied. Zero is the site as this session found it. */
  cursor: number;
  /** Entries retained — applied and undone together. */
  count: number;
  /** True once the depth cap has dropped the oldest operations, so the list no longer reaches back to the start. */
  truncated: boolean;
}

export type CommandHistoryListener = (state: CommandHistoryState) => void;

/** The internal record: the command plus the metadata the panel reads off it. */
interface HistoryRecord {
  readonly command: Command;
  readonly id: number;
  readonly label: string;
  readonly kind: CommandKind;
  readonly at: number;
  steps: number;
}

/**
 * Command-based undo/redo and the session's operations history (spec §7, milestone 4.1–4.2).
 *
 * ## One list and a cursor, not two stacks
 *
 * The obvious implementation of undo/redo is two stacks, and it was this one's. It is replaced here
 * by a single ordered list plus an index, because the two are equivalent for undo and redo and only
 * one of them can answer the question a history *panel* asks: "what has happened this session, in
 * order, and where in it am I?" Two stacks hold that answer upside-down and in halves — the undone
 * operations are reversed relative to the done ones — and the panel would have to reassemble it on
 * every repaint, in the one place a mistake shows up as the list disagreeing with the document.
 *
 * With a cursor, the list *is* the session and `cursor` is a bookmark in it. Undo steps the bookmark
 * back, redo steps it forward, and {@link goto} moves it any distance in either direction — which is
 * click-to-jump, the thing that makes a history list worth showing rather than just a longer pair of
 * buttons. Nothing is deleted on undo, so an operation you stepped past is still there to be read
 * and returned to.
 *
 * Recording a new operation after undoing is the one destructive act: the entries after the cursor
 * become unreachable and are dropped, because the new edit was made against a state they no longer
 * follow from. That is Paint.NET's behaviour, Photoshop's, and every other linear history's — the
 * alternative is a tree, and a tree asks the user to navigate branches they never asked to create.
 *
 * ## Ephemeral by design
 *
 * The history is a record of *this session* and is never serialized. A project file describes a
 * garden, not the path taken to it, and an undo record that outlived the objects it refers to would
 * restore plants into a site that has since been replaced. Opening or starting a site therefore
 * clears it — see `FloraEditor.clearSite`.
 */
export class CommandHistory {
  /** Every operation of the session, oldest first, applied and undone alike. */
  private readonly records: HistoryRecord[] = [];
  /**
   * How many records are applied: the index of the next redo, one past the last undo. Kept as a
   * count rather than as an index into the array so that "nothing applied" is 0 rather than -1.
   */
  private index = 0;
  private nextId = 1;
  /** How many entries the depth cap has dropped off the front, so the panel can say the list is partial. */
  private dropped = 0;
  /** Memoized {@link entries} result, rebuilt only when the list itself changes. */
  private view: readonly HistoryEntry[] | null = null;

  private readonly maxDepth: number;
  private readonly now: () => number;
  private readonly listeners = new Set<CommandHistoryListener>();

  constructor(options: CommandHistoryOptions = {}) {
    this.maxDepth = Math.max(100, options.maxDepth ?? 100);
    this.now = options.now ?? ((): number => Date.now());
  }

  get canUndo(): boolean {
    return this.index > 0;
  }

  get canRedo(): boolean {
    return this.index < this.records.length;
  }

  /** How many operations can be undone. */
  get depth(): number {
    return this.index;
  }

  /** How many are applied — the position in {@link entries}, where 0 is before the first. */
  get cursor(): number {
    return this.index;
  }

  /** How many operations are retained in total, applied and undone. */
  get count(): number {
    return this.records.length;
  }

  /** Whether the depth cap has dropped the oldest operations off the front of the list. */
  get truncated(): boolean {
    return this.dropped > 0;
  }

  /**
   * The session's operations, oldest first. Everything before {@link cursor} is applied; everything
   * from it on has been undone and can be returned to.
   */
  entries(): readonly HistoryEntry[] {
    this.view ??= this.records.map((record) => ({
      id: record.id,
      label: record.label,
      kind: record.kind,
      at: record.at,
      steps: record.steps,
    }));
    return this.view;
  }

  /** Execute a command and record it. */
  execute(command: Command): void {
    command.execute();
    this.record(command);
  }

  /**
   * Record a command that has *already* been applied to the model (e.g. a live drag). Behaves like
   * {@link execute} but without calling `execute()` now.
   */
  push(command: Command): void {
    this.record(command);
  }

  private record(command: Command): void {
    // Anything undone is now unreachable: this edit was made against the state at the cursor, so
    // what followed the old one no longer follows from anything.
    if (this.records.length > this.index) {
      this.records.length = this.index;
      this.view = null;
    }

    const top = this.records[this.index - 1];
    if (
      top &&
      command.mergeKey !== undefined &&
      top.command.mergeKey === command.mergeKey &&
      top.command.mergeWith?.(command)
    ) {
      // Merged into the previous command; no new entry, but the entry now stands for one more step.
      top.steps += 1;
      this.view = null;
      this.emit();
      return;
    }

    this.records.push({
      command,
      id: this.nextId,
      label: command.label,
      kind: command.kind ?? 'edit',
      at: this.now(),
      steps: 1,
    });
    this.nextId += 1;
    this.index = this.records.length;

    // The cap counts *retained* operations, which after the truncation above is the same as the
    // undoable depth — so this is the same bound the two-stack version enforced.
    if (this.records.length > this.maxDepth) {
      const overflow = this.records.length - this.maxDepth;
      this.records.splice(0, overflow);
      this.dropped += overflow;
      this.index = this.records.length;
    }
    this.view = null;
    this.emit();
  }

  undo(): boolean {
    const record = this.records[this.index - 1];
    if (!record) return false;
    record.command.undo();
    this.index -= 1;
    this.emit();
    return true;
  }

  redo(): boolean {
    const record = this.records[this.index];
    if (!record) return false;
    record.command.execute();
    this.index += 1;
    this.emit();
    return true;
  }

  /**
   * Travel to a point in the session: apply or revert whatever it takes to leave exactly `index`
   * operations applied. `0` is the state the session started in; {@link count} is the latest.
   *
   * This is the history panel's click-to-jump, and it is deliberately the *same* commands undo and
   * redo run, in the same order, rather than a snapshot restore. A journey of thirty steps is thirty
   * ordinary undos, so travelling can never reach a state that undoing one at a time could not.
   *
   * Listeners hear once, at the end. A journey is one act from the user's side, and firing per step
   * would have every panel in the HUD rebuild itself thirty times for one click — and would briefly
   * publish intermediate states that nobody asked to see.
   *
   * Returns false when already there.
   */
  goto(index: number): boolean {
    const target = Math.min(Math.max(Math.trunc(index), 0), this.records.length);
    if (target === this.index) return false;
    try {
      while (this.index > target) {
        const record = this.records[this.index - 1];
        if (!record) break;
        record.command.undo();
        this.index -= 1;
      }
      while (this.index < target) {
        const record = this.records[this.index];
        if (!record) break;
        record.command.execute();
        this.index += 1;
      }
    } finally {
      // In `finally` so that a command that throws part-way still leaves the listeners describing
      // where the document actually got to, rather than where it set out for.
      this.emit();
    }
    return true;
  }

  /** Rewind to the state the session started in. */
  rewind(): boolean {
    return this.goto(0);
  }

  /** Fast-forward past everything that has been undone. */
  fastForward(): boolean {
    return this.goto(this.records.length);
  }

  /**
   * Forget the session's operations, keeping the document exactly as it is.
   *
   * Nothing is undone — this drops the *record*, not the work. It is how a document swap avoids
   * leaving undo entries that refer to a site that is no longer open, and it is what the panel's
   * "Clear history" offers for a long session whose early steps are no longer wanted.
   */
  clear(): void {
    this.records.length = 0;
    this.index = 0;
    this.dropped = 0;
    this.view = null;
    this.emit();
  }

  state(): CommandHistoryState {
    const undoTop = this.records[this.index - 1];
    const redoTop = this.records[this.index];
    return {
      canUndo: this.canUndo,
      canRedo: this.canRedo,
      undoLabel: undoTop?.label ?? null,
      redoLabel: redoTop?.label ?? null,
      cursor: this.index,
      count: this.records.length,
      truncated: this.dropped > 0,
    };
  }

  onChange(listener: CommandHistoryListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  private emit(): void {
    const state = this.state();
    for (const listener of this.listeners) {
      listener(state);
    }
  }
}
